import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { classQueryKeys, normalizeSchoolClass } from '../constants/classes';

function syllabusDocId(className, subject) {
  const c = normalizeSchoolClass(className) || className;
  const s = String(subject || '').trim().replace(/\//g, '-');
  return `${c}__${s}`;
}

export async function listSubjects() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'subjects'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function subjectDocId(name) {
  return String(name).trim().toLowerCase().replace(/\s+/g, '_');
}

export async function saveSubject({ id, name, className }) {
  if (!db) throw new Error('Firestore not configured.');
  const docId = id || subjectDocId(name);
  await setDoc(
    doc(db, 'subjects', docId),
    {
      name: String(name).trim(),
      class: className ? normalizeSchoolClass(className) || className : null,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
  return docId;
}

export async function updateSubject({ id, name, className }) {
  if (!db) throw new Error('Firestore not configured.');
  if (!id) throw new Error('Subject id is required.');
  const newName = String(name).trim();
  const newClass = className ? normalizeSchoolClass(className) || className : null;
  const newId = subjectDocId(newName);
  if (newId !== id) {
    await setDoc(doc(db, 'subjects', newId), {
      name: newName,
      class: newClass,
      updatedAt: new Date().toISOString(),
    });
    await deleteDoc(doc(db, 'subjects', id));
    return newId;
  }
  await setDoc(
    doc(db, 'subjects', id),
    { name: newName, class: newClass, updatedAt: new Date().toISOString() },
    { merge: true }
  );
  return id;
}

export async function deleteSubject(id) {
  if (!db) throw new Error('Firestore not configured.');
  await deleteDoc(doc(db, 'subjects', id));
}

export async function listAssessments() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'assessments'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export async function saveAssessment({ name, type, className, subject, maxMarks, date }) {
  if (!db) throw new Error('Firestore not configured.');
  const subj = String(subject).trim().replace(/\W/g, '').slice(0, 16);
  const id = `${Date.now()}_${subj}_${Math.random().toString(36).slice(2, 7)}`;
  const payload = {
    name: String(name).trim(),
    type: type === 'TEST' ? 'TEST' : 'EXAM',
    class: normalizeSchoolClass(className) || className,
    subject: String(subject).trim(),
    maxMarks: Number(maxMarks) || 100,
    date: date || new Date().toISOString().slice(0, 10),
    updatedAt: new Date().toISOString(),
  };
  await setDoc(doc(db, 'assessments', id), payload);
  return { id, ...payload };
}

export async function deleteAssessment(id) {
  if (!db) throw new Error('Firestore not configured.');
  await deleteDoc(doc(db, 'assessments', id));
}

export async function getSyllabus(className, subject) {
  if (!db) return null;
  const snap = await getDoc(doc(db, 'syllabus', syllabusDocId(className, subject)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listSyllabusForClass(className) {
  if (!db) return [];
  const c = normalizeSchoolClass(className) || className;
  const snap = await getDocs(query(collection(db, 'syllabus'), where('class', '==', c)));
  let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (c === 'L.K.G') {
    const legacy = await getDocs(query(collection(db, 'syllabus'), where('class', '==', 'LKG')));
    rows = rows.concat(legacy.docs.map((d) => ({ id: d.id, ...d.data() })));
  }
  if (c === 'NUR') {
    const legacy = await getDocs(query(collection(db, 'syllabus'), where('class', '==', 'Nur')));
    rows = rows.concat(legacy.docs.map((d) => ({ id: d.id, ...d.data() })));
  }
  return rows.sort((a, b) => String(a.subject).localeCompare(String(b.subject)));
}

export async function listAllSyllabus() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'syllabus'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function saveSyllabus({ className, subject, content, updatedBy }) {
  if (!db) throw new Error('Firestore not configured.');
  const id = syllabusDocId(className, subject);
  await setDoc(
    doc(db, 'syllabus', id),
    {
      class: normalizeSchoolClass(className) || className,
      subject: String(subject).trim(),
      content: String(content || ''),
      updatedBy: updatedBy || null,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
  return id;
}

export async function saveAssessmentMark({ assessmentId, admno, studentName, className, subject, score, maxMarks }) {
  if (!db) throw new Error('Firestore not configured.');
  const id = `${assessmentId}_${admno}`;
  await setDoc(
    doc(db, 'assessmentMarks', id),
    {
      assessmentId,
      admno: String(admno),
      studentName,
      class: normalizeSchoolClass(className) || className,
      subject,
      score: Number(score),
      maxMarks: Number(maxMarks) || 100,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
}

export async function listAssessmentMarks(assessmentId) {
  if (!db) return [];
  const q = query(collection(db, 'assessmentMarks'), where('assessmentId', '==', assessmentId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listAssessmentMarksForStudent(admno, studentCsvId) {
  if (!db) return [];
  const ids = [...new Set([admno, studentCsvId].filter(Boolean).map(String))];
  const rows = [];
  for (const id of ids) {
    const q = query(collection(db, 'assessmentMarks'), where('admno', '==', id));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => rows.push({ id: d.id, ...d.data() }));
  }
  const byKey = new Map();
  rows.forEach((r) => byKey.set(r.id, r));
  return [...byKey.values()].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

export async function listAssessmentsForClass(className) {
  const c = normalizeSchoolClass(className) || className;
  const all = await listAssessments();
  return all.filter((a) => classQueryKeys(c).includes(a.class));
}

/** Subjects applicable to a class (class-specific + all-class subjects). */
export function subjectsForClass(subjects, className) {
  if (className === '__ALL__' || className === 'ALL') {
    const byName = new Map();
    subjects.forEach((s) => byName.set(s.name, s));
    return [...byName.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }
  const keys = new Set(classQueryKeys(className));
  return subjects
    .filter((s) => !s.class || keys.has(s.class))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** Whether a subject applies to a given class. */
export function subjectAppliesToClass(subject, className) {
  if (!subject?.class) return true;
  const keys = new Set(classQueryKeys(className));
  return keys.has(subject.class);
}

/** Unique assessment names for a class. */
export function assessmentNamesForClass(assessments, className) {
  const keys = new Set(classQueryKeys(className));
  return [
    ...new Set(
      assessments
        .filter((a) => keys.has(a.class))
        .map((a) => a.name)
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b));
}

export function findAssessment(assessments, className, assessmentName, subjectName) {
  const keys = new Set(classQueryKeys(className));
  return assessments.find(
    (a) => keys.has(a.class) && a.name === assessmentName && a.subject === subjectName
  );
}
